import React, { useEffect, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';
import { foldChar, locatePassage, MATCH_MESSAGES } from './PdfHighlightViewer';

/*
 * DocxHighlightViewer: the Word counterpart of PdfHighlightViewer.
 *
 * DocumentPreview only highlights PDFs. A .docx goes to DocViewer, a plain Word viewer that can neither
 * highlight nor scroll. This renders the .docx in the page, finds the cited passage the same way
 * PdfHighlightViewer does (its locatePassage: the chunk first, then the phrase inside it), highlights it
 * and scrolls to it.
 *
 * Props (same names as PdfHighlightViewer):
 *   docUrl    link to the .docx
 *   text1     table caption
 *   text2     verbatim phrase
 *   text3     whole chunk
 *   fallback  what to show if the file cannot be fetched or rendered (e.g. the existing <DocViewer />)
 */

// =====================================================================
//  DOCUMENT TEXT INDEX
// =====================================================================

const BLOCK_TAGS = new Set([
  'P', 'DIV', 'TD', 'TH', 'TR', 'LI', 'UL', 'OL', 'TABLE', 'TBODY', 'THEAD', 'TFOOT',
  'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'CAPTION', 'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'BLOCKQUOTE',
]);

/**
 * The rendered document as one searchable string, with a map from every character back to the text node
 * (and offset) it came from.
 *
 * Word stores one sentence as many runs, often splitting a word in two ("Marktverfüg" + "barkeit"), so
 * text inside one paragraph is joined WITHOUT a separator. A space goes in only where a block ends (a
 * paragraph, a table cell, a row) or at a line break, which is where a reader sees a gap.
 */
const buildTextIndex = (root) => {
  let concat = '';
  const map = []; // map[i] = { node, offset } | null (a separator that belongs to no text node)
  let needSpace = false;

  const push = (c, node, offset) => {
    concat += c;
    map.push(node ? { node, offset } : null);
  };

  const walk = (el) => {
    Array.from(el.childNodes).forEach((child) => {
      if (child.nodeType === 3) {
        const text = child.nodeValue || '';
        for (let i = 0; i < text.length; i++) {
          for (const c of foldChar(text[i])) {
            if (c === ' ') {
              if (concat && concat[concat.length - 1] !== ' ') push(' ', child, i);
              needSpace = false;
            } else {
              if (needSpace && concat && concat[concat.length - 1] !== ' ') push(' ', null, 0);
              needSpace = false;
              push(c, child, i);
            }
          }
        }
      } else if (child.nodeType === 1) {
        const tag = child.tagName;
        if (tag === 'STYLE' || tag === 'SCRIPT') return;
        if (tag === 'BR') { needSpace = true; return; }
        const block = BLOCK_TAGS.has(tag);
        if (block) needSpace = true;
        walk(child);
        if (block) needSpace = true;
      }
    });
  };

  walk(root);
  return { concat, map };
};

// =====================================================================
//  HIGHLIGHT + SCROLL
// =====================================================================

const MARK_CLASS = 'docx-source-highlight';

/** Highlights the cited passage. Returns { found, marks, kind }. */
const highlightPassage = (root, texts) => {
  const doc = root.ownerDocument;
  const { concat, map } = buildTextIndex(root);

  const m = locatePassage(concat, texts);
  if (!m) {
    console.log('DocxHighlightViewer: no match found');
    return { found: false, marks: [], kind: 'notfound' };
  }

  let s = m.start;
  while (s < m.end && !map[s]) s++;
  let e = m.end - 1;
  while (e > s && !map[e]) e--;
  if (s > e || !map[s]) return { found: false, marks: [], kind: 'notfound' };

  const spans = new Map(); // text node -> [first offset, last offset]
  for (let i = s; i <= e; i++) {
    const hit = map[i];
    if (!hit) continue;
    const cur = spans.get(hit.node);
    if (!cur) spans.set(hit.node, [hit.offset, hit.offset]);
    else { cur[0] = Math.min(cur[0], hit.offset); cur[1] = Math.max(cur[1], hit.offset); }
  }

  const marks = [];
  spans.forEach(([a, b], node) => {
    if (!node.parentNode) return;
    const range = doc.createRange();
    range.setStart(node, a);
    range.setEnd(node, b + 1);
    const mark = doc.createElement('mark');
    mark.className = MARK_CLASS;
    mark.style.cssText = 'background:rgba(255,213,79,0.55);color:inherit;border-radius:2px;padding:0;';
    try {
      range.surroundContents(mark);
      marks.push(mark);
    } catch (err) { /* a range that cannot be wrapped is skipped */ }
  });

  if (!marks.length) return { found: false, marks: [], kind: 'notfound' };
  console.log(`DocxHighlightViewer: matched ${m.kind} -> ${marks.length} fragment(s)`);
  return { found: true, marks, kind: m.kind };
};

/** Scrolls `scroller` (not the page or the modal around it) so `mark` sits about a third of the way down. */
const scrollToMark = (scroller, mark) => {
  if (!scroller || !mark) return;
  const sr = scroller.getBoundingClientRect();
  const mr = mark.getBoundingClientRect();
  const top = Math.max(0, scroller.scrollTop + (mr.top - sr.top) - scroller.clientHeight * 0.3);
  scroller.scrollTo({ top, behavior: 'smooth' });
};

// =====================================================================
//  COMPONENT
// =====================================================================

const bannerBase = { padding: '5px 12px', fontSize: '12px', lineHeight: 1.4, borderBottom: '1px solid #e0e4ea', flexShrink: 0 };
const BANNER_COLORS = {
  phrase: { background: '#fff8e1', color: '#7a5b00' },
  caption: { background: '#fff8e1', color: '#7a5b00' },
  chunk: { background: '#eef4ff', color: '#3538cd' },
  notfound: { background: '#fef3f2', color: '#b42318' },
};

const DocxHighlightViewer = ({ docUrl, text1, text2, text3, fallback = null }) => {
  const hostRef = useRef(null);
  const scrollRef = useRef(null);
  const [phase, setPhase] = useState('loading'); // 'loading' | 'ready' | 'failed'
  const [match, setMatch] = useState(null); // null | 'phrase' | 'chunk' | 'caption' | 'notfound'

  useEffect(() => {
    let cancelled = false;
    const timers = [];

    (async () => {
      try {
        const res = await fetch(docUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buffer = await res.arrayBuffer();
        if (cancelled || !hostRef.current) return;

        hostRef.current.innerHTML = '';
        await renderAsync(buffer, hostRef.current, null, { className: 'docx', inWrapper: true, breakPages: true });
        if (cancelled) return;
        setPhase('ready');

        if (!text1 && !text2 && !text3) return;

        const result = highlightPassage(hostRef.current, { caption: text1, phrase: text2, chunk: text3 });
        setMatch(result.kind);
        if (result.found) {
          // images and fonts settle after the first paint, so scroll again once the layout has stopped moving
          [120, 600].forEach((ms) => timers.push(setTimeout(() => scrollToMark(scrollRef.current, result.marks[0]), ms)));
        }
      } catch (err) {
        console.error('DocxHighlightViewer: could not load the document', err);
        if (!cancelled) setPhase('failed');
      }
    })();

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [docUrl, text1, text2, text3]);

  if (phase === 'failed') {
    return fallback || (
      <div style={{ padding: 24, textAlign: 'center', color: '#b42318', fontSize: 13 }}>
        This Word file could not be opened here.
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {match && <div style={{ ...bannerBase, ...BANNER_COLORS[match] }}>{MATCH_MESSAGES[match]}</div>}
      {phase === 'loading' && <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: '#475467' }}>Loading document...</div>}
      <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflow: 'auto', background: '#e9ecef' }}>
        <div ref={hostRef} />
      </div>
    </div>
  );
};

export default DocxHighlightViewer;
